import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CITY_ANCHORS, cityCelebrationAsset, spriteUrl } from "./gameUtils";
import { Sprite, CheckIcon, HeartIcon } from "./fx";

// The illustrated city: one wide painted plate the child pans across, parallax
// clouds drifting in front of the sky, glowing quest markers over today's
// moments, golden ground glows where kindness already happened, and a rainbow
// + twinkle pass when the whole day is done.

const CLOUDS = [
  { x: 0.1, y: 0.16, scale: 1, dur: 7 },
  { x: 0.33, y: 0.24, scale: 0.7, dur: 9 },
  { x: 0.56, y: 0.14, scale: 1.2, dur: 6.5 },
  { x: 0.8, y: 0.2, scale: 0.85, dur: 8 },
];

const TWINKLES = [
  { x: 0.08, y: 0.42 }, { x: 0.22, y: 0.3 }, { x: 0.38, y: 0.46 }, { x: 0.52, y: 0.26 },
  { x: 0.68, y: 0.42 }, { x: 0.82, y: 0.32 }, { x: 0.93, y: 0.48 },
];

const Marker = ({ scenario, isCompleted, onTap }) => {
  const leftAsset = cityCelebrationAsset(scenario.id);
  return (
    <button
      type="button"
      className={`ch-marker ${isCompleted ? "is-done" : ""}`}
      onClick={onTap}
      aria-label={isCompleted ? `${scenario.title}, done! Tap to play again` : scenario.title}
    >
      {isCompleted ? (
        <span className="ch-marker-done">
          {leftAsset ? (
            <span className="ch-marker-friend">
              <span className="ch-marker-shadow" />
              <Sprite name={leftAsset} />
              <span className="ch-medal ch-medal--heart" aria-hidden="true"><HeartIcon size={15} color="#fff" /></span>
            </span>
          ) : (
            <span className="ch-medal ch-medal--lg" aria-hidden="true"><CheckIcon size={26} /></span>
          )}
        </span>
      ) : (
        <span className="ch-marker-wait">
          <span className="ch-marker-halo" />
          <span className="ch-marker-tile">
            <Sprite name={scenario.iconAsset} />
          </span>
          <span className="ch-marker-tail" />
        </span>
      )}
    </button>
  );
};

const CityScape = ({ scenarios, completedIDs, dayComplete, parallax, onTapHotspot }) => (
  <div className={`ch-cityscape ${dayComplete ? "is-golden" : ""}`}>
    <div className="ch-plate" style={{ backgroundImage: `url(${spriteUrl("ch_city_plate")})` }} />

    <div className="ch-clouds" style={{ transform: `translateX(${parallax * 0.35}px)` }} aria-hidden="true">
      {CLOUDS.map((c, i) => (
        <span
          key={i}
          className="ch-cloud"
          style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%`, width: `${11 * c.scale}%`, animationDuration: `${c.dur}s` }}
        >
          <Sprite name="ch_cloud" />
        </span>
      ))}
    </div>

    <AnimatePresence>
      {dayComplete && (
        <motion.div
          className="ch-golden-hour"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2 }}
          aria-hidden="true"
        >
          <div className="ch-sunset-tint" />
          <div className="ch-horizon-glow" />
          <motion.span
            className="ch-rainbow"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.2 }}
          >
            <Sprite name="ch_rainbow" />
          </motion.span>
          {TWINKLES.map((t, i) => (
            <span key={i} className="ch-twinkle" style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%`, animationDelay: `${i * 0.2}s` }} />
          ))}
        </motion.div>
      )}
    </AnimatePresence>

    {scenarios.map((scenario) => {
      const anchor = CITY_ANCHORS[scenario.anchor];
      const done = completedIDs.has(scenario.id);
      return (
        <div
          key={scenario.id}
          className="ch-hotspot"
          data-scenario={scenario.id}
          style={{ left: `${anchor.x * 100}%`, top: `${anchor.y * 100}%` }}
        >
          <span className={`ch-mood-decal ${done ? "is-done" : ""}`} aria-hidden="true" />
          <Marker scenario={scenario} isCompleted={done} onTap={() => onTapHotspot(scenario)} />
        </div>
      );
    })}
  </div>
);

export default CityScape;
